// AI Generated Component
import { Component, OnInit } from '@angular/core';
import { ParamService } from '@monsido/core/param/param.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import moment from 'moment';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'statistics-sources-summary',
    templateUrl: './summary.component.html',
    standalone: false,
})
export class StatisticsSourcesSummaryComponent implements OnInit {
    SOURCE_TYPES = ['organic', 'direct', 'referring', 'social', 'paid'];
    domain: any;
    itemReferringInformation: any;
    type: string = '';
    remoteData: any;
    totalVisits: number = 0;
    date: any;
    lineChartSeries: any;
    lineChartData: any;
    lineChartColors: any;
    lineChartLabels: any;
    lineChartOverride: any;
    lineChartOptions: any;
    loading: boolean = false;

    constructor (
        private paramService: ParamService,
        private statsRepoService: StatsRepoService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private dateRangeService: DateRangeService,
        private pipeService: PipeService,
    ) {}

    async ngOnInit (): Promise<void> {
        await this.activate();
    }

    async activate (): Promise<void> {
        this.domain = this.sessionService.domain;
        this.itemReferringInformation = {
            externalReffering: {
                header: this.translateService.getString('Referring domains'),
                headerIcon: '',
                footer: this.translateService.getString('External referees overview'),
                labels: [],
                footerUrl: 'base.customer.domain.statistics.sources.domains',
            },
            searchEngines: {
                header: this.translateService.getString('Search engines'),
                headerIcon: '',
                footer: this.translateService.getString('search engines overview'),
                labels: [],
                footerUrl: 'base.customer.domain.statistics.sources.searchEngines',
            },
            social: {
                header: this.translateService.getString('Social Networks'),
                headerIcon: '',
                footer: this.translateService.getString('social network overview'),
                labels: [],
                footerUrl: 'base.customer.domain.statistics.sources.social',
            },
        };
        this.type = 'visits';
        this.remoteData = [];
        this.totalVisits = 0;
        this.date = this.dateRangeService.setRangeDates();

        this.setupChartSettings();
        await this.updateDateRange();
    }

    async updateDateRange (): Promise<void> {
        this.paramService.setParams({
            from: moment(this.date.startDate),
            to: moment(this.date.endDate),
        });
        await this.setupChart();
        this.setupReferrersBoxes();
    }

    setChartData (type: string): void {
        this.type = type;
        this.lineChartData = [];
        const remoteDataParameter = this.SOURCE_TYPES.reduce((param, key) => {
            if (this.remoteData[key].length > this.remoteData[param].length) {
                return key;
            }
            return param;
        }, 'referring');
        const visitParameter = this.mapType(type);
        this.SOURCE_TYPES.forEach((remoteDataParam) => {
            if (Array.isArray(this.remoteData[remoteDataParam])) {
                this.lineChartData.push(
                    this.remoteData[remoteDataParam].map((data) => {
                        if (this.type === 'bounceRate') {
                            return this.bounceRateCalculate(data);
                        } else if (this.type === 'avgVisitTime') {
                            return parseInt(data[visitParameter], 10) || 0;
                        } else {
                            return parseInt(data[visitParameter], 10) || 0;
                        }
                    }),
                );
            } else {
                this.lineChartData.push([]);
            }
        });
        if (this.type === 'bounceRate' || this.type === 'avgVisitTime') {
            this.setLineChart();
        } else {
            this.setBarChart();
        }
        this.lineChartLabels = this.remoteData[remoteDataParameter].map((val) => {
            return moment(val.date).format('ll');
        });
    }

    private setupChartSettings (): void {
        this.lineChartSeries = [
            this.translateService.getString('Search engine'),
            this.translateService.getString('Direct visits'),
            this.translateService.getString('Referring visits'),
            this.translateService.getString('Social visits'),
            this.translateService.getString('Campaign visits'),
        ];
        this.lineChartData = [];
        this.lineChartColors = ['#57BCDB', '#57B754', '#EAA646', '#9500AC', '#D24747'];
        this.lineChartLabels = [];
        this.lineChartOverride = [];
        this.lineChartOptions = {
            tooltips: {
                mode: 'label',
                intersect: false,
                callbacks: {
                    label: (tooltipItem, data) => {
                        if (this.type === 'avgVisitTime') {
                            return (
                                data.datasets[tooltipItem.datasetIndex].label +
                                    ': ' +
                                    this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || 0, '00:00:00')
                            );
                        } else if (this.type === 'bounceRate') {
                            return (
                                data.datasets[tooltipItem.datasetIndex].label +
                                    ': ' +
                                    data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] +
                                    '%'
                            );
                        }
                        return (
                            data.datasets[tooltipItem.datasetIndex].label +
                                ': ' +
                                this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || 0)
                        );
                    },
                },
            },
            scales: {
                yAxes: [
                    {
                        id: 'y-axis-0',
                        type: 'linear',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 5,
                            callback: (value) => {
                                if (this.type === 'avgVisitTime') {
                                    return this.pipeService.get('monNumber')(value, '00:00:00');
                                } else if (this.type === 'bounceRate') {
                                    return parseInt(value) + '%';
                                }
                                return this.pipeService.get('monNumber')(value, '0,0');
                            },
                            min: 0,
                        },
                        stacked: true,
                    },
                ],
                xAxes: [
                    {
                        gridLines: {
                            display: false,
                        },
                        stacked: true,
                    },
                ],
            },
        };
    }

    private async setupChart (): Promise<void> {
        const params = {
            from: this.date.startDate.format('YYYY-MM-DD'),
            to: this.date.endDate.format('YYYY-MM-DD'),
        };
        const promises = [this.getTotalVisits(params)];
        for (let j = 0; j < this.SOURCE_TYPES.length; j++) {
            promises.push(this.getVisitorsForType(this.SOURCE_TYPES[j], params));
        }
        this.loading = true;
        return Promise.all(promises)
            .then(() => {
                this.setChartData(this.type);
            }, () => {})
            .finally(() => {
                this.loading = false;
            });
    }

    private mapType (type: string): string {
        switch (type) {
            case 'uniqueVisits':
                return 'uniq_visitors';
            case 'pageViews':
                return 'pageviews';
            case 'avgVisitTime':
                return 'avg_time';
            case 'bounceRate':
                return 'bounce_rate';
            default:
                return type;
        }
    }

    private setLineChart (): void {
        const lineChartOverride: any = [];
        for (let i = 0; i < this.lineChartSeries.length; i++) {
            lineChartOverride.push({
                type: 'line',
                fill: false,
                borderColor: this.lineChartColors[i],
                borderWidth: 1,
                lineTension: 0,
            });
        }
        this.lineChartOverride = lineChartOverride;
        this.lineChartOptions.scales.yAxes[0].stacked = false;
    }

    private setBarChart (): void {
        const lineChartOverride: any = [];
        for (let i = 0; i < this.lineChartSeries.length; i++) {
            lineChartOverride.push({
                type: 'bar',
                fill: false,
                borderColor: this.lineChartColors[i],
                borderWidth: 1,
            });
        }
        this.lineChartOverride = lineChartOverride;
        this.lineChartOptions.scales.yAxes[0].stacked = true;
    }

    private bounceRateCalculate (data: any): number {
        data = data || {};
        if (isNaN(+data.visits) || isNaN(+data.bounce_rate) || +data.bounce_rate === 0 || +data.visits === 0) {
            return 0;
        }

        return parseInt(((+data.bounce_rate * 100) / +data.visits).toString(), 10);
    }

    private getVisitorsForType (type: string, params: any): Promise<any> {
        return this.statsRepoService.getVisitors(safeClone({ ...params, type: type })).then(
            (data) => {
                this.remoteData[type] = data;
            },
            () => {
                this.remoteData[type] = [];
            },
        );
    }

    private setupReferrersBoxes (): void {
        const params = {
            from: this.date.startDate.format('YYYY-MM-DD'),
            to: this.date.endDate.format('YYYY-MM-DD'),
            limit: 3,
        };

        this.statsRepoService.getReferrers(params).then((data) => {
            this.itemReferringInformation.externalReffering.data = data;
        }, () => {});

        this.statsRepoService.getSearchEngines(params).then((data) => {
            this.itemReferringInformation.searchEngines.data = data;
        }, () => {});

        this.statsRepoService.getSocials(params).then((data) => {
            this.itemReferringInformation.social.data = data;
        }, () => {});
    }

    private getTotalVisits (params: any): Promise<any> {
        return this.statsRepoService.getSummary(params).then((data: any) => {
            this.totalVisits = parseInt(data.visits) || 0;
        }, () => {});
    }
}
